/**
 * Integrytas Group - Base App (MOBILE)
 * Page: APR.CONFIG — mobile-native.
 *
 * Touch-first counterpart of src/pages/admin/ApprovalConfigPage.tsx. A DISTINCT
 * presentation (dense card list + search + facet chips + status pills +
 * bottom-sheet detail + edit sheet) over the SAME data/logic layer only:
 *   - reads via apiDb (apr_config — the generic Approval Workflow engine's config
 *     registry: entity, mode, status column, JSON workflow rules),
 *   - every scalar write (mode / status column / description / active) routes
 *     through the Common Routine via routedUpdate — §2 single write path; never a
 *     direct apiDb write.
 * It never re-renders the desktop grid; §16. Config creation, deletion and the
 * raw workflow-rules JSON editor stay on the desktop — the rules are shown
 * read-only here (mirroring MobileUserEditSheet's read-only roles). The config's
 * own internal_code is its natural identifier (no _tl table), like a currency
 * code — §17.B4's resolve-FK-to-name rule does not apply to a table's own PK.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, Power, X, SquarePen, Workflow } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { useActiveLocale } from '../../lib/use_active_locale';
import { useFormatDateTime } from '../../lib/format_date';
import { routedUpdate } from '../../lib/routed_writes';
import {
  MobilePageHeader, MobileContentCard, MobileCardRow,
  MobileDetailRow, MobileFilterChips, MobileStatusPill,
} from '../components';
import type { AuthSession } from '../../types/auth';
import type { MobilePageComponentProps } from '../mobile_page_components';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'APR.CONFIG';

type ConfigMode = 'WRITE_THROUGH' | 'BLOCK_STAGE';

interface MobileConfigRow {
  approval_config_id: number;
  internal_code: string;
  entity_name: string;
  mode: ConfigMode;
  status_column: string | null;
  workflow_rules: unknown;
  is_active: 'Y' | 'N';
  description: string | null;
  updated_at: string | null;
}

type StatusFacet = 'all' | 'active' | 'inactive';

// CHECK-enum labels via t() labelled constants — never a bare stored literal (§17.B11).
const MODE_OPTIONS: { value: ConfigMode; labelKey: string; fallback: string }[] = [
  { value: 'WRITE_THROUGH', labelKey: 'mobile.apr_config.mode.write_through', fallback: 'Write-through' },
  { value: 'BLOCK_STAGE', labelKey: 'mobile.apr_config.mode.block_stage', fallback: 'Block-stage' },
];

/** Count the sequential approval levels declared in the workflow rules, if any. */
function levelCount(rules: unknown): number | null {
  if (rules && typeof rules === 'object' && 'levels' in rules) {
    const levels = (rules as { levels?: unknown }).levels;
    if (Array.isArray(levels)) return levels.length;
  }
  return null;
}

export function MobileApprovalConfigPage({ session }: MobilePageComponentProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canChange = hasAccess(session, PAGE_CODE, 'change');

  const { t } = useTranslation();
  const activeLocale = useActiveLocale();
  const formatDateTime = useFormatDateTime();
  const [rows, setRows] = useState<MobileConfigRow[]>([]);
  const [filter, setFilter] = useState('');
  const [statusFacet, setStatusFacet] = useState<StatusFacet>('active');
  const [modeFilter, setModeFilter] = useState<ConfigMode[]>([]);
  const [selected, setSelected] = useState<MobileConfigRow | null>(null);
  const [editing, setEditing] = useState<MobileConfigRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const modeLabel = (mode: ConfigMode): string => {
    const opt = MODE_OPTIONS.find((o) => o.value === mode);
    return opt ? t(opt.labelKey, opt.fallback) : mode;
  };

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data, error: e } = await apiDb
        .from('apr_config')
        .select('approval_config_id, internal_code, entity_name, mode, status_column, workflow_rules, is_active, description, updated_at')
        .order('internal_code');
      if (e) throw e;
      setRows((data ?? []) as MobileConfigRow[]);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Re-run on mount (activeLocale kept in deps for parity with the other mobile
  // screens, even though this registry carries no _tl labels of its own).
  useEffect(() => { void reload(); }, [activeLocale]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFacet === 'active' && r.is_active !== 'Y') return false;
      if (statusFacet === 'inactive' && r.is_active !== 'N') return false;
      if (modeFilter.length > 0 && !modeFilter.includes(r.mode)) return false;
      if (!q) return true;
      return (
        r.internal_code.toLowerCase().includes(q) ||
        r.entity_name.toLowerCase().includes(q) ||
        (r.description ?? '').toLowerCase().includes(q)
      );
    });
  }, [rows, filter, statusFacet, modeFilter]);

  const statusFacets: { key: StatusFacet; label: string }[] = [
    { key: 'all', label: t('mobile.apr_config.all', 'All') },
    { key: 'active', label: t('common.boolean.active', 'Active') },
    { key: 'inactive', label: t('common.boolean.inactive', 'Inactive') },
  ];

  const toggleMode = (mode: ConfigMode) =>
    setModeFilter((prev) => (prev.includes(mode) ? prev.filter((m) => m !== mode) : [...prev, mode]));

  const title = (r: MobileConfigRow) => r.description?.trim() || r.internal_code;
  const subtitle = (r: MobileConfigRow) => {
    const parts = [r.entity_name, modeLabel(r.mode)];
    if (r.description?.trim()) parts.unshift(r.internal_code);
    return parts.join(' · ');
  };

  const toggleActive = async (r: MobileConfigRow) => {
    setActionBusy(true);
    setError(null);
    const next = r.is_active === 'Y' ? 'N' : 'Y';
    // §2: route through the Common Routine (forensic stamp + audit + hooks).
    const { error: e } = await routedUpdate(session, 'apr_config', 'approval_config_id', r.approval_config_id, { is_active: next });
    setActionBusy(false);
    if (e) { setError(e.message); return; }
    setSelected(null);
    void reload();
  };

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.apr_config.title', 'Approval Configs')}
        description={t('mobile.apr_config.description', 'Which entities route through the approval engine, and how each intercepts writes.')}
      />

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.apr_config.search', 'Search approval configs')}
          aria-label={t('mobile.apr_config.search', 'Search approval configs')}
        />
      </div>

      <MobileFilterChips
        chips={statusFacets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => statusFacet === v}
        onSelect={(v) => setStatusFacet(v as StatusFacet)}
      />

      <MobileFilterChips
        chips={MODE_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey, o.fallback) }))}
        isActive={(v) => modeFilter.includes(v as ConfigMode)}
        onSelect={(v) => toggleMode(v as ConfigMode)}
      />

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.apr_config.none', 'No approval configs match')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((r) => (
            <MobileCardRow
              key={r.approval_config_id}
              lead={<Workflow />}
              leadTone={r.mode === 'WRITE_THROUGH' ? 'blue' : 'amber'}
              title={title(r)}
              subtitle={subtitle(r)}
              trailing={
                <MobileStatusPill tone={r.is_active === 'Y' ? 'ok' : 'no'}>
                  {r.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                </MobileStatusPill>
              }
              chevron
              onClick={() => setSelected(r)}
            />
          ))}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={() => { if (!actionBusy) setSelected(null); }} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.apr_config.detail', 'Approval config detail')}>
        <div className="m-grabber" />
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><Workflow size={20} /></span>
              <span>
                <div className="m-nm">{title(selected)}</div>
                <div className="m-em">{selected.internal_code}</div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow
                label={t('mobile.apr_config.status', 'Status')}
                value={<MobileStatusPill tone={selected.is_active === 'Y' ? 'ok' : 'no'}>{selected.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}</MobileStatusPill>}
              />
              <MobileDetailRow label={t('mobile.apr_config.mode_lbl', 'Mode')} value={modeLabel(selected.mode)} />
              <MobileDetailRow label={t('mobile.apr_config.entity', 'Entity')} value={<span className="m-code">{selected.entity_name}</span>} />
              <MobileDetailRow
                label={t('mobile.apr_config.status_column', 'Status column')}
                value={selected.status_column ? <span className="m-code">{selected.status_column}</span> : t('common.na', 'N/A')}
              />
              <MobileDetailRow label={t('mobile.apr_config.levels', 'Approval levels')} value={levelCount(selected.workflow_rules) ?? t('common.na', 'N/A')} />
              {selected.description?.trim() && (
                <MobileDetailRow label={t('mobile.apr_config.description', 'Description')} value={selected.description} />
              )}
              {selected.updated_at && (
                <MobileDetailRow label={t('mobile.apr_config.updated', 'Last updated')} value={formatDateTime(selected.updated_at)} />
              )}
            </MobileContentCard>
            <div className="m-actionbar">
              {canChange && (<button
                type="button"
                className="m-btn m-btn-primary"
                disabled={actionBusy}
                onClick={() => { setEditing(selected); setSelected(null); }}
              >
                <SquarePen size={16} />
                {t('mobile.apr_config.edit', 'Edit')}
              </button>)}
              {canChange && (<button
                type="button"
                className={'m-btn ' + (selected.is_active === 'Y' ? 'm-btn-danger' : 'm-btn-ghost')}
                disabled={actionBusy}
                onClick={() => void toggleActive(selected)}
              >
                <Power size={16} />
                {selected.is_active === 'Y' ? t('users.list.actions.deactivate', 'Deactivate') : t('users.list.actions.activate', 'Activate')}
              </button>)}
              <button type="button" className="m-btn m-btn-ghost" disabled={actionBusy} onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>

      <MobileApprovalConfigEditSheet
        session={session}
        row={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); void reload(); }}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Edit sheet — the config's scalar fields (mode, status column, description,
// active), saved through the Common Routine (routedUpdate) — §2. The raw
// workflow-rules JSON editor stays on the desktop, so the rules are shown
// read-only here (mirrors MobileUserEditSheet's read-only roles). WRITE_THROUGH
// requires a status column, mirroring the desktop validation rule.
// ---------------------------------------------------------------------------

interface MobileApprovalConfigEditSheetProps {
  session: AuthSession;
  row: MobileConfigRow | null;
  onClose: () => void;
  onSaved: () => void;
}

function MobileApprovalConfigEditSheet({ session, row, onClose, onSaved }: MobileApprovalConfigEditSheetProps) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<ConfigMode>('WRITE_THROUGH');
  const [statusColumn, setStatusColumn] = useState('');
  const [description, setDescription] = useState('');
  const [active, setActive] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Seed the form whenever a different row is opened.
  useEffect(() => {
    if (!row) return;
    setMode(row.mode);
    setStatusColumn(row.status_column ?? '');
    setDescription(row.description ?? '');
    setActive(row.is_active === 'Y');
    setError(null);
  }, [row]);

  const levels = row ? levelCount(row.workflow_rules) : null;

  const save = async () => {
    if (!row) return;
    if (mode === 'WRITE_THROUGH' && !statusColumn.trim()) {
      setError(t('mobile.apr_config.err.status_col_required', 'Write-through needs a status column.'));
      return;
    }
    setBusy(true);
    setError(null);
    // §2: route through the Common Routine (forensic stamp + audit + hooks).
    const { error: e } = await routedUpdate(session, 'apr_config', 'approval_config_id', row.approval_config_id, {
      mode,
      status_column: mode === 'BLOCK_STAGE' ? null : (statusColumn.trim() || null),
      description: description.trim() || null,
      is_active: active ? 'Y' : 'N',
    });
    setBusy(false);
    if (e) { setError(e.message); return; }
    onSaved();
  };

  const open = row !== null;

  return (
    <>
      <div className={'m-scrim' + (open ? ' m-open' : '')} onClick={() => { if (!busy) onClose(); }} />
      <div className={'m-sheet' + (open ? ' m-open' : '')} aria-label={t('mobile.apr_config.edit_sheet', 'Edit approval config')}>
        <div className="m-grabber" />
        {row && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><Workflow size={20} /></span>
              <span>
                <div className="m-nm">{t('mobile.apr_config.edit_title', 'Edit approval config')}</div>
                <div className="m-em">{row.internal_code}</div>
              </span>
            </div>

            <div className="m-fieldcard">
              <div className="m-formrow">
                <label>{t('mobile.apr_config.entity', 'Entity')}</label>
                <div className="m-formrow-ro">
                  <span className="m-code">{row.entity_name}</span>
                  <span className="m-formrow-hint"> · {t('mobile.apr_config.entity_desktop', 'change on desktop')}</span>
                </div>
              </div>
              <div className="m-formrow">
                <label htmlFor="m-apr-mode">{t('mobile.apr_config.mode_lbl', 'Mode')}</label>
                <select
                  id="m-apr-mode"
                  className="m-sel"
                  value={mode}
                  onChange={(e) => setMode(e.target.value as ConfigMode)}
                >
                  {MODE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{t(o.labelKey, o.fallback)}</option>)}
                </select>
              </div>
              <div className="m-formrow">
                <label htmlFor="m-apr-statuscol">
                  {t('mobile.apr_config.status_column', 'Status column')}
                  {mode === 'WRITE_THROUGH' ? ' · ' + t('mobile.apr_config.required', 'required') : ' · ' + t('common.na', 'N/A')}
                </label>
                <input
                  id="m-apr-statuscol"
                  className="m-inp"
                  value={statusColumn}
                  disabled={mode === 'BLOCK_STAGE'}
                  onChange={(e) => setStatusColumn(e.target.value)}
                  placeholder="approval_status_code"
                />
              </div>
              <div className="m-formrow">
                <label htmlFor="m-apr-desc">{t('mobile.apr_config.description', 'Description')}</label>
                <input
                  id="m-apr-desc"
                  className="m-inp"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
              <div className="m-formrow m-formrow-inline">
                <span>
                  <div className="m-formrow-l">{t('common.boolean.active', 'Active')}</div>
                  <div className="m-formrow-s">{t('mobile.apr_config.active_hint', 'Intercept writes to this entity')}</div>
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={active}
                  className={'m-switch ' + (active ? 'm-on' : 'm-off')}
                  onClick={() => setActive((v) => !v)}
                >
                  <span className="m-knob" />
                </button>
              </div>
              <div className="m-formrow">
                <label>{t('mobile.apr_config.workflow_rules', 'Workflow rules')}</label>
                <div className="m-formrow-ro">
                  {levels === null
                    ? t('mobile.apr_config.no_levels', 'No levels defined')
                    : t('mobile.apr_config.levels_count', { count: levels, defaultValue: `${levels} levels` })}
                  <span className="m-formrow-hint"> · {t('mobile.apr_config.rules_desktop', 'edit on desktop')}</span>
                </div>
              </div>
            </div>

            {error && <div className="m-card"><div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div></div>}

            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-primary" disabled={busy} onClick={() => void save()}>
                {busy ? t('common.saving', 'Saving…') : t('common.save', 'Save changes')}
              </button>
              <button type="button" className="m-btn m-btn-ghost" disabled={busy} onClick={onClose} aria-label={t('common.cancel', 'Cancel')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
